"use client"

import { useState } from "react"
import styled, { css } from "styled-components"

const MinistriesSection = styled.section`
  background-color: #ffffff;
  color: #333333;
  padding: 80px 20px;
  position: relative;
`

const Content = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
`

const SectionTitle = styled.h2`
  margin-bottom: 20px;
  font-size: 2.5rem;
  color: #cc0000;
  text-align: center;
`

const Introduction = styled.p`
  font-size: 1.2rem;
  max-width: 800px;
  margin: 0 auto 40px;
  line-height: 1.6;
  text-align: center;
  color: #333333;
`

const MinistriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
`

const MinistryCard = styled.div`
  background-color: #ffffff;
  border-radius: 15px;
  padding: 30px;
  text-align: left;
  box-shadow: 0 4px 6px rgba(204, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  border: 2px solid #cc0000;

  &:hover, &:focus-visible {
    transform: translateY(-5px);
    box-shadow: 0 6px 12px rgba(204, 0, 0, 0.2);
  }

  ${(props) =>
    props.featured &&
    css`
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    align-items: center;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  `}
`

const MinistryIcon = styled.div`
  color: #cc0000;
  margin-bottom: 15px;
  font-size: 2rem;
`

const MinistryTitle = styled.h3`
  margin: 0 0 10px;
  font-size: 1.5rem;
  color: #cc0000;
`

const MinistryDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #333333;
`

const ExpandedContent = styled.div`
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #cc0000;
  color: #333333;
`

const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
  gap: 10px;
  flex-wrap: wrap;
`

const FilterButton = styled.button`
  background-color: ${(props) => (props.active ? "#cc0000" : "transparent")};
  color: ${(props) => (props.active ? "white" : "#cc0000")};
  border: 2px solid #cc0000;
  padding: 10px 20px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover, &:focus-visible {
    background-color: #cc0000;
    color: white;
  }
`

const Ministries = () => {
  const [expandedMinistry, setExpandedMinistry] = useState(null)
  const [filter, setFilter] = useState("all")

  const ministriesData = [
    {
      title: "Men Ministry",
      description: "Empowering our men with faith-based activities and leadership training.",
      icon: "👨",
      expanded:
        "Join us for monthly meetings, retreats, and community service projects. We focus on spiritual growth, fellowship, and developing Christ-centered leadership skills.",
      category: "adult",
    },
    {
      title: "Women Ministry",
      description: "Nurturing and supporting women in their spiritual journey and personal growth.",
      icon: "👩",
      expanded:
        "Our women's ministry offers Bible studies, prayer groups, and annual conferences. We provide a supportive community for women to grow in faith and friendship.",
      category: "adult",
    },
    {
      title: "Youth Ministry",
      description: "Guiding and inspiring young people to develop a strong faith and character.",
      icon: "🧑",
      expanded:
        "Weekly youth nights, summer camps, and mission trips help our youth build a strong foundation in Christ. We combine fun activities with meaningful spiritual discussions.",
      category: "youth",
    },
    {
      title: "Children Ministry",
      description: "Providing a fun and safe environment for children to learn about God's love.",
      icon: "👶",
      expanded:
        "Our children's ministry includes Sunday School, Vacation Bible School, and family events. We use age-appropriate curriculum to teach biblical truths in an engaging way.",
      category: "children",
    },
    {
      title: "Single Woman",
      description: "Extending God's love to our community through service and compassion.",
      icon: "👱‍♀️",
      expanded:
        "From local food banks to global missions, we actively serve our community and beyond. Join us in making a difference and sharing God's love through practical acts of service.",
      category: "service",
    },
    {
      title: "Outreach Ministry",
      description: "Extending God's love to our community through service and compassion.",
      icon: "🤝",
      expanded:
        "From local food banks to global missions, we actively serve our community and beyond. Join us in making a difference and sharing God's love through practical acts of service.",
      category: "service",
    },
    {
      title: "Music Ministry",
      description: "Praising God through uplifting music and worship experiences.",
      icon: "🎵",
      expanded:
        "Our music ministry includes the church choir, worship band, and seasonal productions. We believe in using our musical gifts to glorify God and enhance the worship experience.",
      category: "worship",
    },
  ]

  const filteredMinistries =
    filter === "all" ? ministriesData : ministriesData.filter((ministry) => ministry.category === filter)

  return (
    <MinistriesSection id="ministries">
      <Content>
        <SectionTitle>Our Ministries</SectionTitle>
        <Introduction>
          At our church, we believe in nurturing faith through various ministries that cater to different groups and
          interests. Each ministry is designed to help you grow spiritually, build meaningful relationships, and serve
          our community.
        </Introduction>
        <FilterContainer>
          <FilterButton active={filter === "all"} onClick={() => setFilter("all")}>
            All
          </FilterButton>
          <FilterButton active={filter === "adult"} onClick={() => setFilter("adult")}>
            Adult
          </FilterButton>
          <FilterButton active={filter === "youth"} onClick={() => setFilter("youth")}>
            Youth
          </FilterButton>
          <FilterButton active={filter === "children"} onClick={() => setFilter("children")}>
            Children
          </FilterButton>
          <FilterButton active={filter === "service"} onClick={() => setFilter("service")}>
            Service
          </FilterButton>
          <FilterButton active={filter === "worship"} onClick={() => setFilter("worship")}>
            Worship
          </FilterButton>
        </FilterContainer>
        <MinistriesGrid>
          {filteredMinistries.map((ministry, index) => (
            <MinistryCard
              key={index}
              featured={index === 0 && filter === "all"}
              onClick={() => setExpandedMinistry(expandedMinistry === index ? null : index)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  setExpandedMinistry(expandedMinistry === index ? null : index)
                }
              }}
              tabIndex={0}
              role="button"
              aria-expanded={expandedMinistry === index}
            >
              <div>
                <MinistryIcon>{ministry.icon}</MinistryIcon>
                <MinistryTitle>{ministry.title}</MinistryTitle>
                <MinistryDescription>{ministry.description}</MinistryDescription>
                {expandedMinistry === index && <ExpandedContent>{ministry.expanded}</ExpandedContent>}
              </div>
              {index === 0 && filter === "all" && (
                <img
                  src="/images/coc.jpg"
                  alt="Featured Ministry"
                  style={{ width: "100%", height: "auto", borderRadius: "10px" }}
                />
              )}
            </MinistryCard>
          ))}
        </MinistriesGrid>
      </Content>
    </MinistriesSection>
  )
}

export default Ministries

